// Pure per-park aggregation helpers (no DB / store deps) so they're unit-testable in
// isolation. Used by HunterStatsService and HunterStatsStore.
import { splitReferences } from '@/utils/potaReference';
import type { QsoType, LocalParkStat } from '@/types';

// Fold one QSO into a per-park stats map (count + first/last-worked dates). A QSO can
// reference multiple parks (a "2-fer", stored comma-joined e.g. "US-1,US-2" by ADIF
// import); each park earns a contact, so credit them all.
export function accumulateQso(map: Map<string, LocalParkStat>, qso: QsoType): void {
  const refs = splitReferences(qso.potaRef);
  if (refs.length === 0) return;
  // Prefer the full ISO timestamp; fall back to the dashed qsoDate. Both sort
  // lexically, so string min/max gives correct first/last-worked.
  const date = qso.qsoDateTime || qso.qsoDate || undefined;
  for (const ref of refs) {
    const cur = map.get(ref);
    if (cur) {
      cur.count++;
      if (date) {
        if (!cur.firstDate || date < cur.firstDate) cur.firstDate = date;
        if (!cur.lastDate || date > cur.lastDate) cur.lastDate = date;
      }
    } else {
      map.set(ref, { count: 1, firstDate: date, lastDate: date });
    }
  }
}

// Per-park stats for a single QSO array (e.g. the open logbook's live qsoList).
export function statsFromQsos(qsos: QsoType[]): Map<string, LocalParkStat> {
  const map = new Map<string, LocalParkStat>();
  for (const qso of qsos) accumulateQso(map, qso);
  return map;
}

// Merge two stat maps without double-counting: counts add, first = min, last = max.
// Overlays the open logbook's live stats on top of the other-logbooks base.
export function mergeStats(
  a: Map<string, LocalParkStat>,
  b: Map<string, LocalParkStat>
): Map<string, LocalParkStat> {
  // Always return a fully detached map (cloned entries) so callers can mutate the result
  // without touching either input - important now that cached maps may be merged.
  const out = new Map<string, LocalParkStat>();
  for (const [ref, stat] of a) out.set(ref, { ...stat });
  for (const [ref, s] of b) {
    const cur = out.get(ref);
    if (!cur) {
      out.set(ref, { ...s });
      continue;
    }
    out.set(ref, {
      count: cur.count + s.count,
      firstDate: minDate(cur.firstDate, s.firstDate),
      lastDate: maxDate(cur.lastDate, s.lastDate),
    });
  }
  return out;
}

function minDate(a?: string, b?: string): string | undefined {
  if (!a) return b;
  if (!b) return a;
  return a < b ? a : b;
}

function maxDate(a?: string, b?: string): string | undefined {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
}
